import { memo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart as BC,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
} from "recharts";
import { AxisDomain } from "recharts/types/util/types";

import {
  PRIMARY_CHART_COLOR,
  SECONDARY_CHART_COLOR,
} from "features/ui/charts/constants";
import { DataElement, MarginProps } from "features/ui/charts/types";

import CustomYAxisTick from "./CustomYAxisTick";

interface SparklineBarChartProps {
  data: DataElement[];
  xAxisKey: string;
  yAxisKey: string;
  height: number;
  width: number;
  margin?: MarginProps;
  xAxisProps?: XAxisProps;
  yAxisProps?: YAxisProps;
  yAxisWidth?: number;
  yAxisDomain?: AxisDomain;
  maxBarWidth?: number;
  stackedBarKey?: string;
}

// memoized component to prevent unnecessary re-renders
const SparklineBarChart = memo(
  ({
    data,
    xAxisKey,
    yAxisKey,
    height,
    width,
    margin = { top: 5, right: 10, bottom: 10, left: 10 },
    xAxisProps,
    yAxisProps,
    yAxisWidth = 0,
    yAxisDomain = [0, "auto"],
    maxBarWidth = 10,
    stackedBarKey,
  }: SparklineBarChartProps) => {
    const { t } = useTranslation("common");

    if (!data || !Array.isArray(data) || data.length === 0) {
      return <div>{t("no_data")}</div>;
    }

    // calculate max Y value considering both bars if stacked
    const maxY = stackedBarKey
      ? Math.max(
          ...data.map((d) => (d[yAxisKey] || 0) + (d[stackedBarKey] || 0))
        )
      : Math.max(...data.map((d) => d[yAxisKey]));

    return (
      <BC
        data={data}
        margin={margin}
        width={width}
        height={height}
        style={{ cursor: "inherit" }}
      >
        <Bar
          dataKey={yAxisKey}
          fill={PRIMARY_CHART_COLOR}
          isAnimationActive={false}
          maxBarSize={maxBarWidth}
          stackId={stackedBarKey ? "stack" : undefined}
        />
        {stackedBarKey && (
          <Bar
            dataKey={stackedBarKey}
            fill={SECONDARY_CHART_COLOR}
            isAnimationActive={false}
            maxBarSize={maxBarWidth}
            stackId="stack"
          />
        )}
        <YAxis
          width={yAxisWidth}
          domain={yAxisDomain}
          ticks={[maxY]}
          axisLine={false}
          tickLine={false}
          tick={<CustomYAxisTick />}
          orientation="left"
          {...yAxisProps}
        />
        <XAxis dataKey={xAxisKey} {...xAxisProps} />
      </BC>
    );
  }
);

export default SparklineBarChart;
